/** Default hunt for an absent anchor. Short: an anchor that is not in the DOM
 *  by the time its step becomes current is almost always one this reviewer does
 *  not have (no 4K ladder, no overlays, no flight set), and every millisecond
 *  here is dead air on the card. */
export const ANCHOR_WAIT_MS = 600;

/** The map rail (basemap swatch, follow, compass, footprint toggle, panorama
 *  markers) is built inside mountViewer's async setup, after the map, the tiles
 *  and the track have loaded. Those steps hunt for much longer. */
export const MAP_RAIL_WAIT_MS = 2500;

export function anchorSelector(id: string): string {
  return `[data-tour="${id}"]`;
}

/** True when `el` sits inside a closed `<details>` (its own summary does
 *  not count). Chrome keeps a folded section's children at their last
 *  laid-out size rather than collapsing them to nothing, so a box check
 *  alone would let the tour spotlight a list nobody can see. */
function foldedAway(el: Element): boolean {
  let node: Element | null = el;
  while (node !== null) {
    const details: HTMLDetailsElement | null = node.parentElement?.closest("details") ?? null;
    if (details === null) return false;
    const isSummary = node.tagName === "SUMMARY" && node.parentElement === details;
    if (!details.open && !isSummary) return true;
    node = details;
  }
  return false;
}

/** An anchor that exists but is `hidden`, folded away, not rendered, or has
 *  no box counts as absent — the viewer marks unsupported buttons `hidden`,
 *  the save-state pill unmounts entirely when idle, and the Flights page
 *  folds whole sections. */
export function isUsableAnchor(el: Element): boolean {
  if ((el as HTMLElement).hidden) return false;
  if (foldedAway(el)) return false;
  const check = (el as { checkVisibility?: (o?: object) => boolean }).checkVisibility;
  if (typeof check === "function" && !check.call(el, { visibilityProperty: true })) return false;
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0;
}

export type AnchorPick = "first" | "last";

/** The synchronous half of `resolveTarget`: is a usable anchor in the DOM right
 *  now? The overlay calls this before its first paint of a step. Leaflet
 *  appends the newest popup last in its pane, so a lesson about the card just
 *  opened asks for the last usable match rather than the first. */
export function findAnchor(id: string, pick: AnchorPick = "first"): HTMLElement | null {
  const all = Array.from(document.querySelectorAll(anchorSelector(id)))
    .filter(isUsableAnchor) as HTMLElement[];
  if (all.length === 0) return null;
  return (pick === "last" ? all[all.length - 1] : all[0]) ?? null;
}

export function resolveTarget(
  id: string,
  opts: { timeoutMs?: number; signal?: AbortSignal } = {},
): Promise<HTMLElement | null> {
  const timeoutMs = opts.timeoutMs ?? ANCHOR_WAIT_MS;
  const now = findAnchor(id);
  if (now !== null) return Promise.resolve(now);
  return new Promise((resolve) => {
    let done = false;
    const finish = (el: HTMLElement | null): void => {
      if (done) return;
      done = true;
      observer.disconnect();
      clearTimeout(timer);
      opts.signal?.removeEventListener("abort", onAbort);
      resolve(el);
    };
    const onAbort = (): void => finish(null);
    const observer = new MutationObserver(() => {
      const el = findAnchor(id);
      if (el !== null) finish(el);
    });
    observer.observe(document.documentElement, {
      childList: true, subtree: true, attributes: true, attributeFilter: ["hidden", "data-tour"],
    });
    const timer = setTimeout(() => finish(null), timeoutMs);
    if (opts.signal !== undefined) {
      if (opts.signal.aborted) onAbort();
      else opts.signal.addEventListener("abort", onAbort);
    }
  });
}
